import React, { useState, useEffect, useCallback, useRef } from 'react';
// Cache bust: 2025-08-20
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  useDroppable,
  useDraggable,
  closestCenter,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { API_ENDPOINTS } from '../config/apiRouting';
import { useAuth } from '../contexts/AuthContext';
import './MenuBoardConfigurator.css';

// Enhanced Image Component with Error Handling and Retries
interface EnhancedImageProps {
  src?: string;
  alt: string;
  style?: React.CSSProperties;
  className?: string;
  placeholder?: string;
  maxRetries?: number;
}

const EnhancedImage: React.FC<EnhancedImageProps> = ({ 
  src, 
  alt, 
  style, 
  className = '',
  placeholder = '[FOOD]',
  maxRetries = 3
}) => {
  const [currentSrc, setCurrentSrc] = useState(src);
  const [retryCount, setRetryCount] = useState(0);
  const [hasError, setHasError] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  
  // Reset error state when src changes
  useEffect(() => {
    if (src !== currentSrc) {
      setCurrentSrc(src);
      setRetryCount(0);
      setHasError(false);
      setIsRetrying(false);
    }
  }, [src, currentSrc]);

  const handleError = useCallback(() => {
    console.log(`Image load failed (attempt ${retryCount + 1}/${maxRetries}):`, currentSrc);
    
    if (retryCount < maxRetries - 1) {
      // Retry with cache busting
      setIsRetrying(true);
      const timestamp = Date.now();
      const separator = currentSrc?.includes('?') ? '&' : '?';
      const retryUrl = currentSrc ? `${currentSrc}${separator}retry=${timestamp}` : undefined;
      
      setRetryCount(prev => prev + 1);
      setTimeout(() => {
        setCurrentSrc(retryUrl);
        setIsRetrying(false);
      }, 1000); // Brief delay before retry
    } else {
      console.log(`Image permanently failed after ${maxRetries} attempts:`, currentSrc);
      setHasError(true);
      setIsRetrying(false);
    }
  }, [currentSrc, retryCount, maxRetries]);

  const handleLoad = useCallback(() => {
    if (retryCount > 0) {
      console.log(`Image loaded successfully after ${retryCount} retries:`, currentSrc);
    }
    setIsRetrying(false);
  }, [currentSrc, retryCount]);

  if (!currentSrc || hasError) {
    return (
      <div 
        className={`image-placeholder ${isRetrying ? 'retrying' : ''} ${className}`}
        style={{
          ...style,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f3f4f6',
          color: '#9ca3af',
          fontSize: style?.fontSize || '2rem',
          fontWeight: 'bold'
        }}
        title={hasError ? `Failed to load image after ${maxRetries} attempts: ${alt}` : isRetrying ? 'Retrying image load...' : 'No image available'}
      >
        {isRetrying ? 'Loading...' : placeholder}
      </div>
    );
  }

  return (
    <img
      src={currentSrc}
      alt={alt}
      style={style}
      className={className}
      onError={handleError}
      onLoad={handleLoad}
      loading="lazy"
    />
  );
};

// Types
interface MenuBoard {
  menuBoardId: string;
  enterpriseId: string;
  locationId: string;
  screenName: string;
  screenType: 'main' | 'drive-thru' | 'beer' | 'dessert' | 'promotional';
  gridConfiguration: {
    rows: number;
    columns: number;
    aspectRatio: '16:9' | '4:3' | '9:16' | 'custom';
    cellPadding: number;
    backgroundColor: string;
  };
  displaySettings: {
    autoRefreshInterval: number;
    transitionEffect: 'fade' | 'slide' | 'none';
    defaultFontSize: 'small' | 'medium' | 'large' | 'extra-large';
    theme: 'dark' | 'light' | 'custom';
  };
  orderConfirmationBox?: {
    enabled: boolean;
    position: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
    width: number;
    height: number;
    opacity: number;
  };
  isActive: boolean;
  items?: MenuBoardItem[];
}

interface MenuBoardItem {
  itemId?: string;
  menuItemId?: string;
  position: string; // "row-column" format
  gridSpan: {
    rowSpan: number;
    colSpan: number;
  };
  contentType: 'menu-item' | 'video' | 'image' | 'text' | 'promotion';
  displayType: 'name' | 'name-price' | 'name-desc' | 'name-desc-price' | 'name-desc-price-photo' | 'large-photo-price';
  customContent?: {
    text: string;
    backgroundColor: string;
    textColor: string;
    fontSize: string;
    alignment: 'left' | 'center' | 'right';
  };
  mediaContent?: {
    mediaUrl: string;
    mediaType: 'video' | 'image';
    autoPlay?: boolean;
    loop?: boolean;
    volume?: number;
    duration?: number;
  };
  styling: {
    backgroundColor?: string;
    textColor?: string;
    borderColor?: string;
    borderWidth?: number;
    borderRadius?: number;
    opacity?: number;
    animation?: 'none' | 'pulse' | 'slide-in' | 'fade-in';
  };
  isActive: boolean;
  priority: number;
}

interface MenuItem {
  menuItemId: string;
  name: string;
  description?: string;
  category: string;
  price?: number;
  basePrice?: number;
  imageUrl?: string;
}

interface DragItem {
  type: 'menu-item' | 'media' | 'text';
  item: MenuItem | { type: 'video' | 'image' | 'text'; content?: any };
}

// Drag and drop types
const ItemTypes = {
  MENU_ITEM: 'menu-item',
  MEDIA: 'media',
  TEXT: 'text',
  BOARD_ITEM: 'board-item'
};

// Context Menu Component
interface ContextMenuProps {
  x: number;
  y: number;
  onClose: () => void;
  onSelectDisplayType: (displayType: string) => void;
  currentDisplayType?: string;
}

const ContextMenu: React.FC<ContextMenuProps> = ({ x, y, onClose, onSelectDisplayType, currentDisplayType }) => {
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  const displayOptions = [
    { key: 'name', label: 'Name Only' },
    { key: 'name-price', label: 'Name + Price' },
    { key: 'name-desc', label: 'Name + Description' },
    { key: 'name-desc-price', label: 'Name + Description + Price' },
    { key: 'name-desc-price-photo', label: 'Name + Description + Price + Photo' },
    { key: 'large-photo-price', label: 'Large Photo + Price' }
  ];

  return (
    <div
      ref={menuRef}
      className="context-menu"
      style={{ left: x, top: y }}
    >
      <div className="context-menu-header">Display Options</div>
      {displayOptions.map(option => (
        <div
          key={option.key}
          className={`context-menu-item ${currentDisplayType === option.key ? 'active' : ''}`}
          onClick={() => {
            onSelectDisplayType(option.key);
            onClose();
          }}
        >
          <span>{option.label}</span>
        </div>
      ))}
      <div className="context-menu-separator" />
      <div className="context-menu-item danger" onClick={onClose}>
        <span className="context-menu-icon">×</span>
        <span>Remove Item</span>
      </div>
    </div>
  );
};

// Draggable Menu Item Component
interface DraggableMenuItemProps {
  item: MenuItem;
}

const DraggableMenuItem: React.FC<DraggableMenuItemProps> = ({ item }) => {
  console.log('DraggableMenuItem - item structure:', 
    'menuItemId:', item.menuItemId,
    'name:', item.name,
    'keys:', Object.keys(item)
  );

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    isDragging,
  } = useDraggable({
    id: `menu-item-${item.menuItemId}`,
    data: { type: 'menu-item', item }
  });

  const style = transform ? {
    transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
  } : undefined;

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
      className={`draggable-menu-item ${isDragging ? 'dragging' : ''}`}
    >
      <div className="menu-item-image">
        <EnhancedImage
          src={item.imageUrl}
          alt={item.name}
          style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '4px' }}
          placeholder="FOOD"
        />
      </div>
      <div className="menu-item-info">
        <div className="menu-item-name">{item.name}</div>
        <div className="menu-item-category">{item.category}</div>
        <div className="menu-item-price">${(item.price || item.basePrice || 0).toFixed(2)}</div>
      </div>
    </div>
  );
};

// Grid Cell Component
interface GridCellProps {
  row: number;
  col: number;
  item?: MenuBoardItem;
  menuItems: MenuItem[];
  onDrop: (row: number, col: number, dragItem: DragItem) => void;
  onContextMenu: (event: React.MouseEvent, item: MenuBoardItem) => void;
  onItemClick: (item: MenuBoardItem) => void;
}

// Draggable Board Item Component
interface DraggableBoardItemProps {
  item: MenuBoardItem;
  menuItems: MenuItem[];
  onContextMenu: (event: React.MouseEvent, item: MenuBoardItem) => void;
  renderItemContent: (item: MenuBoardItem, menuItem?: MenuItem) => JSX.Element;
}

const DraggableBoardItem: React.FC<DraggableBoardItemProps> = ({ 
  item, 
  menuItems, 
  onContextMenu, 
  renderItemContent 
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    isDragging,
  } = useDraggable({
    id: `board-item-${item.position}`,
    data: {
      type: 'board-item',
      item: item
    }
  });

  const style = transform ? {
    transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 1
  } : {};

  // Find associated menu item
  let menuItem = item.contentType === 'menu-item' && item.menuItemId 
    ? menuItems.find(mi => String(mi.menuItemId) === String(item.menuItemId))
    : undefined;
  
  // Debug logging for menu item matching
  if (item.contentType === 'menu-item') {
    console.log('Debug: Menu item lookup',
      'looking_for_id:', item.menuItemId, 
      'found_item:', menuItem ? 'YES' : 'NO',
      'available_ids:', menuItems.map(mi => mi.menuItemId).slice(0, 5),
      'total_menu_items:', menuItems.length
    );
    
    if (!menuItem && menuItems.length > 0) {
      console.log('Menu item NOT found. Checking alternatives...');
      
      // Try to find with exact string match
      const exactMatch = menuItems.find(mi => 
        String(mi.menuItemId) === String(item.menuItemId)
      );
      if (exactMatch) {
        console.log('Found exact string match, using it');
        menuItem = exactMatch;
      } else {
        // Show available items for debugging
        console.log('Available menu items sample:', 
          menuItems.slice(0, 3).map(mi => ({ id: mi.menuItemId, name: mi.name }))
        );
      }
    }
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
      className={`draggable-board-item ${isDragging ? 'dragging' : ''}`}
      onContextMenu={(e) => onContextMenu(e, item)}
    >
      {renderItemContent(item, menuItem)}
    </div>
  );
};

const GridCell: React.FC<GridCellProps> = ({ row, col, item, menuItems, onDrop, onContextMenu, onItemClick }) => {
  const position = `${row}-${col}`;
  const { isOver, setNodeRef } = useDroppable({
    id: `grid-cell-${position}`,
    data: { row, col, position }
  });

  const renderItemContent = (item: MenuBoardItem, menuItem?: MenuItem) => {
    if (!menuItem && item.contentType === 'menu-item') {
      return (
        <div className="item-loading" style={{ color: 'var(--text-secondary)' }}>
          Loading menu item...
        </div>
      );
    }

    switch (item.contentType) {
      case 'menu-item':
        return renderMenuItemContent(item, menuItem!);
      case 'video':
      case 'image':
        return renderMediaContent(item);
      case 'text':
      case 'promotion':
        return renderTextContent(item);
      default:
        return (
          <div className="item-placeholder" style={{ color: 'var(--text-tertiary)' }}>
            Empty Cell
          </div>
        );
    }
  };


  const renderMenuItemContent = (item: MenuBoardItem, menuItem: MenuItem): JSX.Element => {
    const { displayType } = item;
    
    switch (displayType) {
      case 'name':
        return (
          <div className="menu-item-content name-only">
            <div className="item-name">{menuItem.name}</div>
          </div>
        );
      
      case 'name-price':
        return (
          <div className="menu-item-content name-price" style={{ width: '100%', height: '100%', padding: '0.75rem', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', textAlign: 'center' }}>
            <div className="item-name" style={{ fontSize: '1rem', fontWeight: '600', color: '#212529' }}>{menuItem.name}</div>
            <div className="item-price" style={{ fontSize: '1.2rem', fontWeight: '700', color: '#007bff' }}>${(menuItem.price || menuItem.basePrice || 0).toFixed(2)}</div>
          </div>
        );
      
      case 'name-desc':
        return (
          <div className="menu-item-content name-desc">
            <div className="item-name">{menuItem.name}</div>
            <div className="item-description">{menuItem.description}</div>
          </div>
        );
      
      case 'name-desc-price':
        return (
          <div className="menu-item-content name-desc-price">
            <div className="item-name">{menuItem.name}</div>
            <div className="item-description">{menuItem.description}</div>
            <div className="item-price">${(menuItem.price || menuItem.basePrice || 0).toFixed(2)}</div>
          </div>
        );
      
      case 'name-desc-price-photo':
        return (
          <div className="menu-item-content name-desc-price-photo">
            <div className="item-photo" style={{ position: 'relative', overflow: 'hidden' }}>
              <EnhancedImage
                src={menuItem.imageUrl}
                alt={menuItem.name}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                placeholder="FOOD"
              />
            </div>
            <div className="item-text">
              <div className="item-name">{menuItem.name}</div>
              <div className="item-description">{menuItem.description}</div>
              <div className="item-price">${(menuItem.price || menuItem.basePrice || 0).toFixed(2)}</div>
            </div>
          </div>
        );
      
      case 'large-photo-price':
        return (
          <div className="menu-item-content large-photo-price" style={{ position: 'relative', overflow: 'hidden' }}>
            <div className="item-large-photo" style={{ width: '100%', height: '100%', position: 'relative' }}>
              <EnhancedImage
                src={menuItem.imageUrl}
                alt={menuItem.name}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                placeholder="FOOD"
              />
            </div>
            <div className="item-overlay" style={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              background: 'linear-gradient(transparent, rgba(0,0,0,0.7))',
              color: 'white',
              padding: '1rem'
            }}>
              <div className="item-name" style={{ fontWeight: '600', marginBottom: '0.25rem' }}>{menuItem.name}</div>
              <div className="item-price" style={{ fontSize: '1.1rem', fontWeight: '700' }}>${(menuItem.price || menuItem.basePrice || 0).toFixed(2)}</div>
            </div>
          </div>
        );
      
      default:
        return renderMenuItemContent({ ...item, displayType: 'name-price' }, menuItem);
    }
  };

  const renderMediaContent = (item: MenuBoardItem) => {
    const { mediaContent } = item;
    
    if (!mediaContent) {
      return (
        <div className="media-placeholder">
          <div className="media-icon">VID</div>
          <div className="media-text">Video/Image Placeholder</div>
        </div>
      );
    }

    if (mediaContent.mediaType === 'video') {
      return (
        <div className="media-content video-content">
          <video
            src={mediaContent.mediaUrl}
            autoPlay={mediaContent.autoPlay}
            loop={mediaContent.loop}
            muted={mediaContent.volume === 0}
            controls={false}
          />
        </div>
      );
    } else {
      return (
        <div className="media-content image-content">
          <img
            src={mediaContent.mediaUrl}
            alt="Media content"
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        </div>
      );
    }
  };

  const renderTextContent = (item: MenuBoardItem) => {
    const { customContent } = item;
    
    if (!customContent) {
      return (
        <div className="text-placeholder">
          <div className="text-icon">T</div>
          <div className="text-label">Custom Text</div>
        </div>
      );
    }

    return (
      <div 
        className="text-content"
        style={{
          backgroundColor: customContent.backgroundColor,
          color: customContent.textColor,
          fontSize: customContent.fontSize,
          textAlign: customContent.alignment as any
        }}
      >
        {customContent.text}
      </div>
    );
  };

  return (
    <div
      ref={setNodeRef}
      className={`grid-cell ${isOver ? 'drag-over' : ''} ${item ? 'has-item' : 'empty'}`}
      data-position={`${row}-${col}`}
      onClick={() => item && onItemClick(item)}
      onContextMenu={(e) => {
        e.preventDefault();
        if (item) {
          onContextMenu(e, item);
        }
      }}
      style={{
        gridRow: item?.gridSpan.rowSpan ? `span ${item.gridSpan.rowSpan}` : undefined,
        gridColumn: item?.gridSpan.colSpan ? `span ${item.gridSpan.colSpan}` : undefined,
        backgroundColor: item?.styling.backgroundColor || (item ? '#f8f9fa' : '#e9ecef'),
        borderColor: item?.styling.borderColor || '#dee2e6',
        borderWidth: item?.styling.borderWidth || 1,
        borderRadius: item?.styling.borderRadius || 4,
        opacity: item?.styling.opacity || 1,
        color: item ? '#212529' : '#6c757d'
      }}
    >
      {item ? (
        <DraggableBoardItem 
          item={item} 
          menuItems={menuItems}
          onContextMenu={onContextMenu}
          renderItemContent={renderItemContent}
        />
      ) : (
        <div className="cell-drop-zone" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%', color: '#6c757d', fontSize: '0.9rem', textAlign: 'center', fontWeight: '500' }}>
          <div className="drop-hint" style={{ opacity: '0.7' }}>Drop item here</div>
        </div>
      )}
    </div>
  );
};

// Main Menu Board Configurator Component
interface MenuBoardConfiguratorProps {
  enterpriseId: string;
  selectedLocationId?: string;
}

const MenuBoardConfigurator: React.FC<MenuBoardConfiguratorProps> = ({
  enterpriseId,
  selectedLocationId
}) => {
  const { getIdToken } = useAuth();
  
  // State
  const [menuBoards, setMenuBoards] = useState<MenuBoard[]>([]);
  const [selectedBoard, setSelectedBoard] = useState<MenuBoard | null>(null);
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [contextMenu, setContextMenu] = useState<{
    x: number;
    y: number;
    item: MenuBoardItem;
  } | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('All');
  const [searchTerm, setSearchTerm] = useState('');
  const [activeId, setActiveId] = useState<string | null>(null);
  
  // Filter state with default data
  const [enterprises, setEnterprises] = useState<Array<{enterpriseId: string, name: string}>>([]);
  const [locations, setLocations] = useState<Array<{storeId: string, storeName: string}>>([]);
  const [selectedEnterpriseFilter, setSelectedEnterpriseFilter] = useState<string>(enterpriseId);
  const [selectedLocationFilter, setSelectedLocationFilter] = useState<string>(selectedLocationId || '');
  
  
  // Error state
  const [apiErrors, setApiErrors] = useState<string[]>([]);
  
  // Board name editing state
  const [isEditingBoardName, setIsEditingBoardName] = useState(false);
  const [editingBoardName, setEditingBoardName] = useState('');
  
  // Sidebar collapse state - starts collapsed by default
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(true);

  // Sensors for drag and drop
  const mouseSensor = useSensor(MouseSensor, {
    activationConstraint: {
      distance: 8,
    },
  });
  const touchSensor = useSensor(TouchSensor, {
    activationConstraint: {
      delay: 300,
      tolerance: 8,
    },
  });
  const sensors = useSensors(mouseSensor, touchSensor);

  // Load data
  useEffect(() => {
    const loadAllData = async () => {
      await loadEnterprises();
      await loadLocations();
      await loadMenuBoards();
      await loadMenuItems();
      
      // API call completed
    };
    
    loadAllData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (selectedEnterpriseFilter !== enterpriseId) {
      loadLocations();
      loadMenuBoards();
      loadMenuItems();
    }
  }, [selectedEnterpriseFilter]);

  useEffect(() => {
    if (selectedLocationFilter) {
      loadMenuBoards();
    }
  }, [selectedLocationFilter]);

  const loadEnterprises = async () => {
    try {
      console.log('Loading enterprises from:', API_ENDPOINTS.enterprises());
      
      const idToken = await getIdToken();
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };
      if (idToken) {
        headers['Authorization'] = `Bearer ${idToken}`;
      }
      
      const response = await fetch(API_ENDPOINTS.enterprises(), {
        headers
      });
      console.log('Response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('Full response data:', data);
        
        // Handle different response formats
        let enterpriseList = [];
        
        // Check for various possible data structures
        if (Array.isArray(data)) {
          enterpriseList = data;
        } else if (data && typeof data === 'object') {
          // Check common property names
          if (Array.isArray(data.enterprises)) {
            enterpriseList = data.enterprises;
          } else if (Array.isArray(data.items)) {
            enterpriseList = data.items;
          } else if (Array.isArray(data.data)) {
            enterpriseList = data.data;
          } else if (Array.isArray(data.results)) {
            enterpriseList = data.results;
          } else {
            // If it's a single object that looks like an enterprise, wrap it in an array
            if (data.enterpriseId && data.enterpriseName) {
              enterpriseList = [data];
            }
          }
        }
        
        console.log('Raw data type:', typeof data);
        console.log('Raw data keys:', data ? Object.keys(data) : 'null');
        console.log('Raw data content:', JSON.stringify(data, null, 2));
        console.log('Parsed enterprise list:', enterpriseList);
        console.log('Enterprise list length:', enterpriseList.length);
        
        setEnterprises(enterpriseList);
        
        if (enterpriseList.length === 0) {
          console.error('No enterprises found in API response');
          setApiErrors(prev => [...prev, 'No enterprises found in API response']);
        }
      } else {
        console.error('Failed to load enterprises:', response.status, response.statusText);
        const errorText = await response.text();
        console.error('Error response:', errorText);
        setApiErrors(prev => [...prev, `Enterprise API failed (${response.status})`]);
      }
    } catch (error) {
      console.error('Error loading enterprises:', error);
      setApiErrors(prev => [...prev, 'Enterprise API connection failed']);
    }
  };

  const loadLocations = async () => {
    try {
      console.log('Loading locations for enterprise:', selectedEnterpriseFilter);
      
      const idToken = await getIdToken();
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };
      if (idToken) {
        headers['Authorization'] = `Bearer ${idToken}`;
      }
      
      const response = await fetch(API_ENDPOINTS.enterpriseStores(selectedEnterpriseFilter), {
        headers
      });
      console.log('Response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('Full response data:', data);
        
        // Handle different response formats
        let storeList = [];
        
        // Check for various possible data structures
        if (Array.isArray(data)) {
          storeList = data;
        } else if (data && typeof data === 'object') {
          // Check common property names
          if (Array.isArray(data.stores)) {
            storeList = data.stores;
          } else if (Array.isArray(data.items)) {
            storeList = data.items;
          } else if (Array.isArray(data.data)) {
            storeList = data.data;
          } else if (Array.isArray(data.results)) {
            storeList = data.results;
          } else if (Array.isArray(data.locations)) {
            storeList = data.locations;
          } else {
            // If it's a single object that looks like a store, wrap it in an array
            if (data.storeId && data.storeName) {
              storeList = [data];
            }
          }
        }
        
        console.log('Locations loaded:', storeList);
        console.log('Raw data type:', typeof data);
        console.log('Raw data keys:', data ? Object.keys(data) : 'null');
        console.log('Raw data content:', JSON.stringify(data, null, 2));
        console.log('Parsed store list:', storeList);
        console.log('Store list length:', storeList.length);
        
        setLocations(storeList);
        
        if (storeList.length === 0) {
          console.error('No locations found in API response');
          setApiErrors(prev => [...prev, 'No locations found in API response']);
        }
      } else {
        console.error('Failed to load locations:', response.status, response.statusText);
        const errorText = await response.text();
        console.error('Error response:', errorText);
      }
    } catch (error) {
      console.error('Error loading locations:', error);
    }
  };

  const loadMenuBoards = async () => {
    try {
      setLoading(true);
      const currentEnterprise = selectedEnterpriseFilter || enterpriseId;
      console.log('Loading menu boards for enterprise:', currentEnterprise);
      const url = API_ENDPOINTS.menuBoardsByEnterprise(currentEnterprise);
      console.log('Menu boards URL:', url);
      
      const idToken = await getIdToken();
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };
      if (idToken) {
        headers['Authorization'] = `Bearer ${idToken}`;
      }
      
      const response = await fetch(url, {
        headers
      });
      console.log('Response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('Full response data:', JSON.stringify(data, null, 2));
        
        // Handle different response formats
        const boardList = data.menuBoards || data.items || data || [];
        console.log('Menu boards loaded:', boardList.length, 'boards');
        
        // Check if boards have items
        boardList.forEach((board: any, index: number) => {
          console.log(`Board ${index} (${board.screenName || board.menuBoardId}) has items:`, !!board.items, 'Count:', board.items?.length || 0);
          if (board.items && board.items.length > 0) {
            console.log(`Board ${index} items:`, board.items.map((item: any) => ({
              itemId: item.itemId,
              position: item.position,
              contentType: item.contentType
            })));
          }
        });
        
        // If it's an array directly, use it
        if (Array.isArray(data)) {
          setMenuBoards(data);
          if (!selectedBoard && data.length > 0) {
            console.log('Setting initial selected board with items:', data[0].items?.length || 0);
            setSelectedBoard(data[0]);
          }
        } else {
          setMenuBoards(boardList);
          if (!selectedBoard && boardList.length > 0) {
            console.log('Setting initial selected board with items:', boardList[0].items?.length || 0);
            setSelectedBoard(boardList[0]);
          }
        }
      } else {
        console.error('Failed to load menu boards:', response.status, response.statusText);
        const errorText = await response.text();
        console.error('Error response:', errorText);
      }
    } catch (error) {
      console.error('Error loading menu boards:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMenuItems = async () => {
    try {
      const currentEnterprise = selectedEnterpriseFilter || enterpriseId;
      console.log('Loading menu items for enterprise:', currentEnterprise);
      const idToken = await getIdToken();
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };
      if (idToken) {
        headers['Authorization'] = `Bearer ${idToken}`;
      }
      
      const response = await fetch(API_ENDPOINTS.menuItems(currentEnterprise), {
        headers
      });
      console.log('Response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('Full response data:', data);
        
        // Handle different response formats like other APIs
        let menuItemsList = [];
        
        if (Array.isArray(data)) {
          menuItemsList = data;
        } else if (data && typeof data === 'object') {
          if (Array.isArray(data.menuItems)) {
            menuItemsList = data.menuItems;
          } else if (Array.isArray(data.data)) {
            menuItemsList = data.data;
          } else if (Array.isArray(data.items)) {
            menuItemsList = data.items;
          }
        }
        
        console.log('Raw data type:', typeof data);
        console.log('Raw data keys:', data ? Object.keys(data) : 'null');
        console.log('Raw data content:', JSON.stringify(data, null, 2));
        console.log('Parsed menu items:', menuItemsList);
        console.log('Menu items length:', menuItemsList.length);
        
        setMenuItems(menuItemsList);
        
        if (menuItemsList.length === 0) {
          console.error('No menu items found in API response');
        }
      } else {
        console.error('Failed to load menu items:', response.status, response.statusText);
        const errorText = await response.text();
        console.error('Error response:', errorText);
      }
    } catch (error) {
      console.error('Error loading menu items:', error);
    }
  };

  // Handle drag and drop
  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  }, []);

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);

    console.log('Drag end event:', { active: active.id, over: over?.id, selectedBoard: !!selectedBoard });

    if (!over || !selectedBoard) {
      console.log('No drop target or selected board');
      return;
    }

    // Get the drop target data
    const overData = over.data.current;
    console.log('Over data:', overData);
    if (!overData || overData.row === undefined || overData.col === undefined) {
      console.log('Invalid drop target data');
      return;
    }

    const { row, col } = overData;
    const position = `${row}-${col}`;
    
    // Check if position is already occupied
    const existingItem = selectedBoard.items?.find(item => item.position === position);
    if (existingItem) {
      console.log('Position already occupied:', position);
      return;
    }
    
    console.log('Position available:', position);

    // Get dragged item data
    const activeData = active.data.current;
    if (!activeData) return;

    // Debug dragged item data
    console.log('Active data type:', activeData.type);
    if (activeData.type === 'menu-item') {
      console.log('Menu item data:',
        'menuItemId:', activeData.item?.menuItemId,
        'name:', activeData.item?.name,
        'keys:', Object.keys(activeData.item || {})
      );
    }

    // Create new menu board item
    const newItem: MenuBoardItem = {
      itemId: `MBI-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`, // Generate unique itemId
      menuItemId: activeData.type === 'menu-item' ? activeData.item.menuItemId : undefined,
      position,
      gridSpan: { rowSpan: 1, colSpan: 1 },
      contentType: activeData.type,
      displayType: 'name-price',
      styling: {},
      isActive: true,
      priority: 100
    };
    
    console.log('Creating new item:', { 
      menuItemId: newItem.menuItemId,
      contentType: newItem.contentType,
      position: newItem.position,
      positionType: typeof newItem.position,
      activeDataType: activeData.type
    });
    
    console.log('Full new item object:', JSON.stringify(newItem, null, 2));

    // Check if this is a move operation (item already exists on board)
    const existingItems = selectedBoard.items || [];
    const isMoving = activeData.type === 'board-item' && activeData.item;
    
    let updatedItems;
    if (isMoving) {
      // Remove item from old position and add to new position
      updatedItems = existingItems
        .filter(item => item.position !== activeData.item.position)
        .concat(newItem);
    } else {
      // Just add new item
      updatedItems = [...existingItems, newItem];
    }
    
    const updatedBoard = {
      ...selectedBoard,
      items: updatedItems
    };

    setSelectedBoard(updatedBoard);
    updateMenuBoard(updatedBoard);
  }, [selectedBoard]);

  // Handle context menu
  const handleContextMenu = useCallback((event: React.MouseEvent, item: MenuBoardItem) => {
    event.preventDefault();
    setContextMenu({
      x: event.clientX,
      y: event.clientY,
      item
    });
  }, []);

  const handleDisplayTypeChange = useCallback((displayType: string) => {
    if (!selectedBoard || !contextMenu) return;

    const updatedItems = selectedBoard.items?.map(item =>
      item === contextMenu.item
        ? { ...item, displayType: displayType as any }
        : item
    ) || [];

    const updatedBoard = {
      ...selectedBoard,
      items: updatedItems
    };

    setSelectedBoard(updatedBoard);
    updateMenuBoard(updatedBoard);
    setContextMenu(null);
  }, [selectedBoard, contextMenu]);

  // Reload selected board from server to ensure UI sync
  const reloadSelectedBoard = async (menuBoardId: string) => {
    try {
      console.log('Reloading board from server:', menuBoardId);
      const idToken = await getIdToken();
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };
      if (idToken) {
        headers['Authorization'] = `Bearer ${idToken}`;
      }
      
      const response = await fetch(API_ENDPOINTS.menuBoard(menuBoardId), {
        headers
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Board reloaded from server - Raw response:', JSON.stringify(data, null, 2));
        
        // Handle different response formats
        const boardData = data.menuBoard || data;
        console.log('Board data after format handling:', JSON.stringify(boardData, null, 2));
        console.log('Setting reloaded board with items:', boardData.items?.length || 0);
        
        // Debug the exact structure of items
        if (boardData.items && boardData.items.length > 0) {
          console.log('Detailed item inspection:', boardData.items.map((item: any, index: number) => ({
            index,
            itemId: item.itemId,
            menuItemId: item.menuItemId,
            position: item.position,
            contentType: item.contentType,
            displayType: item.displayType,
            keys: Object.keys(item)
          })));
        } else {
          console.log('Board reloaded but no items found!');
          console.log('Items property exists?', 'items' in boardData);
          console.log('Items value:', boardData.items);
          console.log('Items type:', typeof boardData.items);
        }
        
        setSelectedBoard(boardData);
        setMenuBoards(prev => prev.map(b => 
          b.menuBoardId === menuBoardId ? boardData : b
        ));
        
        // Final verification of what was set
        console.log('Board has been set. Final check - Board has items:', !!boardData.items, 'Count:', boardData.items?.length || 0);
      } else {
        console.error('Failed to reload board:', response.status);
        const errorText = await response.text();
        console.error('Error details:', errorText);
      }
    } catch (error) {
      console.error('Error reloading board:', error);
    }
  };

  // Update menu board
  const updateMenuBoard = async (board: MenuBoard) => {
    try {
      console.log('Updating entire menu board for:', board.menuBoardId);
      console.log('Board items to save:', board.items?.length || 0);
      
      const idToken = await getIdToken();
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };
      if (idToken) {
        headers['Authorization'] = `Bearer ${idToken}`;
      }
      
      // First, update board configuration (without items)
      const { items, ...boardConfig } = board;
      const boardResponse = await fetch(API_ENDPOINTS.menuBoard(board.menuBoardId), {
        method: 'PUT',
        headers,
        body: JSON.stringify(boardConfig)
      });

      if (!boardResponse.ok) {
        console.error('Failed to update menu board config:', boardResponse.status, boardResponse.statusText);
        const errorText = await boardResponse.text();
        console.error('Error details:', errorText);
        alert(`Failed to save menu board configuration: ${boardResponse.status} ${boardResponse.statusText}`);
        return;
      }

      // Then, update board items separately
      if (items && items.length > 0) {
        console.log('Updating menu board items...');
        const itemsResponse = await fetch(API_ENDPOINTS.menuBoardItems(board.menuBoardId), {
          method: 'PUT',
          headers,
          body: JSON.stringify({ items })
        });

        if (!itemsResponse.ok) {
          console.error('Failed to update menu board items:', itemsResponse.status, itemsResponse.statusText);
          const errorText = await itemsResponse.text();
          console.error('Items error details:', errorText);
          alert(`Failed to save menu board items: ${itemsResponse.status} ${itemsResponse.statusText}`);
          return;
        }

        const itemsData = await itemsResponse.json();
        console.log('Menu board items updated:', itemsData);
      }

      console.log('Menu board updated successfully');
      
      // Wait a brief moment for database consistency before reloading
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Force reload the board from server to ensure UI is in sync
      await reloadSelectedBoard(board.menuBoardId);
      
    } catch (error) {
      console.error('Error updating menu board:', error);
      alert(`Error saving menu board: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  // Save board name
  const saveBoardName = async () => {
    if (!selectedBoard || !editingBoardName.trim()) return;
    
    try {
      // Extract items to preserve them, only send board config
      const { items, ...boardConfig } = selectedBoard;
      const updatedBoardConfig = {
        ...boardConfig,
        screenName: editingBoardName.trim()
      };
      
      console.log('Updating board name via:', API_ENDPOINTS.menuBoard(selectedBoard.menuBoardId));
      
      const idToken = await getIdToken();
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };
      if (idToken) {
        headers['Authorization'] = `Bearer ${idToken}`;
      }
      
      const response = await fetch(API_ENDPOINTS.menuBoard(selectedBoard.menuBoardId), {
        method: 'PUT',
        headers,
        body: JSON.stringify(updatedBoardConfig)
      });

      if (response.ok) {
        const data = await response.json();
        // Preserve the existing items when updating the board state
        const updatedBoardWithItems = {
          ...data.menuBoard,
          items: selectedBoard.items // Keep the current items
        };
        setSelectedBoard(updatedBoardWithItems);
        setMenuBoards(prev => prev.map(board => 
          board.menuBoardId === selectedBoard.menuBoardId ? updatedBoardWithItems : board
        ));
        setIsEditingBoardName(false);
        console.log('Board name updated successfully');
      } else {
        console.error('Failed to update board name:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('Error updating board name:', error);
    }
  };

  // Create new menu board
  const createNewMenuBoard = async () => {
    try {
      console.log('Creating new menu board for enterprise:', selectedEnterpriseFilter || enterpriseId);
      const newBoard = {
        enterpriseId: selectedEnterpriseFilter || enterpriseId,
        locationId: selectedLocationFilter || selectedLocationId || '',
        screenName: 'New Menu Board',
        screenType: 'main',
        gridConfiguration: {
          rows: 4,
          columns: 6,
          aspectRatio: '16:9',
          cellPadding: 8,
          backgroundColor: '#ffffff'
        },
        displaySettings: {
          autoRefreshInterval: 30,
          transitionEffect: 'fade',
          defaultFontSize: 'large',
          theme: 'dark'
        },
        isActive: true
      };
      
      const idToken = await getIdToken();
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };
      if (idToken) {
        headers['Authorization'] = `Bearer ${idToken}`;
      }
      
      const response = await fetch(API_ENDPOINTS.menuBoards(), {
        method: 'POST',
        headers,
        body: JSON.stringify(newBoard)
      });

      if (response.ok) {
        const data = await response.json();
        console.log('New menu board created:', data.menuBoard);
        setMenuBoards(prev => [...prev, data.menuBoard]);
        setSelectedBoard(data.menuBoard);
      } else {
        console.error('Failed to create menu board:', response.status, response.statusText);
        const errorText = await response.text();
        console.error('Error details:', errorText);
      }
    } catch (error) {
      console.error('Error creating menu board:', error);
    }
  };

  // Delete menu board
  const deleteMenuBoard = async (menuBoardId: string) => {
    try {
      console.log('Deleting menu board:', menuBoardId);
      
      const idToken = await getIdToken();
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };
      if (idToken) {
        headers['Authorization'] = `Bearer ${idToken}`;
      }
      
      const response = await fetch(API_ENDPOINTS.menuBoard(menuBoardId), {
        method: 'DELETE',
        headers
      });

      if (response.ok) {
        console.log('Menu board deleted successfully');
        setMenuBoards(prev => prev.filter(board => board.menuBoardId !== menuBoardId));
        if (selectedBoard?.menuBoardId === menuBoardId) {
          setSelectedBoard(null);
        }
      } else {
        console.error('Failed to delete menu board:', response.status, response.statusText);
        const errorText = await response.text();
        console.error('Error details:', errorText);
      }
    } catch (error) {
      console.error('Error deleting menu board:', error);
    }
  };

  // Filter menu items
  const filteredMenuItems = menuItems.filter(item => {
    const matchesCategory = selectedCategory === 'All' || item.category === selectedCategory;
    const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.description?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const categories = ['All', ...Array.from(new Set(menuItems.map(item => item.category)))];

  // Render grid
  const renderGrid = () => {
    if (!selectedBoard) return null;

    const { rows, columns } = selectedBoard.gridConfiguration;
    const cells = [];

    // Debug logging to understand the issue
    console.log('renderGrid called with board:', selectedBoard.menuBoardId);
    console.log('Board has items:', selectedBoard.items?.length || 0);
    if (selectedBoard.items && selectedBoard.items.length > 0) {
      console.log('Item positions:', selectedBoard.items.map(item => `${item.position} (${item.menuItemId})`));
    }

    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < columns; col++) {
        const position = `${row}-${col}`;
        const item = selectedBoard.items?.find(item => item.position === position);
        
        // Debug specific position lookups
        if (position === '0-0' || position === '0-1' || position === '1-0') {
          console.log(`Looking for item at position ${position}:`, item ? `Found item ${item.menuItemId}` : 'No item found');
        }
        
        cells.push(
          <GridCell
            key={position}
            row={row}
            col={col}
            item={item}
            menuItems={menuItems}
            onDrop={() => {}} // Keep for compatibility even though we use DndContext
            onContextMenu={handleContextMenu}
            onItemClick={(item) => console.log('Item clicked:', item)}
          />
        );
      }
    }

    return (
      <div
        className="menu-board-grid"
        style={{
          gridTemplateRows: `repeat(${rows}, 1fr)`,
          gridTemplateColumns: `repeat(${columns}, 1fr)`,
          backgroundColor: selectedBoard.gridConfiguration.backgroundColor || '#ffffff',
          gap: selectedBoard.gridConfiguration.cellPadding
        }}
      >
        {cells}
      </div>
    );
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div className="menu-board-configurator">
        {/* Header */}
        <div className="configurator-header">
          <h1>Digital Menu Board Configurator</h1>
          <div className="header-actions">
            <button
              className="btn btn-primary"
              onClick={createNewMenuBoard}
              disabled={loading}
            >
New Menu Board
            </button>
            <select
              value={selectedBoard?.menuBoardId || ''}
              onChange={(e) => {
                const board = menuBoards.find(b => b.menuBoardId === e.target.value);
                setSelectedBoard(board || null);
              }}
            >
              <option value="">Select Menu Board</option>
              {menuBoards.map(board => (
                <option key={board.menuBoardId} value={board.menuBoardId}>
                  {board.screenName} ({board.screenType})
                </option>
              ))}
            </select>
          </div>
        </div>
        
        {/* Error Messages */}
        {apiErrors.length > 0 && (
          <div style={{
            padding: '10px',
            background: '#fee',
            border: '1px solid #f88',
            borderRadius: '4px',
            marginBottom: '10px',
            fontSize: '14px'
          }}>
            <strong>API Errors:</strong>
            {apiErrors.map((error, index) => (
              <div key={index} style={{marginTop: '5px'}}>{error}</div>
            ))}
          </div>
        )}
        
        {/* Image Loading Information Panel */}
        <div style={{
          padding: '12px',
          background: 'linear-gradient(135deg, #e3f2fd 0%, #f3e5f5 100%)',
          border: '1px solid #90caf9',
          borderRadius: '8px',
          marginBottom: '16px',
          fontSize: '13px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
            <span style={{ marginRight: '8px', fontSize: '16px' }}>[IMG]</span>
            <strong style={{ color: '#1565c0' }}>Image Loading Information</strong>
          </div>
          <div style={{ color: '#424242', lineHeight: '1.4' }}>
            <div><strong>Preview Images:</strong> Some menu item images may show placeholders due to expired AI-generated URLs (this is normal behavior).</div>
            <div style={{ marginTop: '4px' }}><strong>Loading:</strong> Images are retried automatically up to 3 times with improved error handling.</div>
            <div style={{ marginTop: '4px' }}><strong>Placeholders:</strong> Friendly placeholders (FOOD) are shown when images cannot be loaded to maintain visual consistency.</div>
          </div>
        </div>
        
        {/* Debug Info */}
        <div style={{padding: '10px', background: '#f0f0f0', fontSize: '12px', fontFamily: 'monospace'}}>
          Debug: Enterprises={enterprises.length}, Locations={locations.length}, MenuBoards={menuBoards.length}, SelectedEnterprise={selectedEnterpriseFilter}, SelectedLocation={selectedLocationFilter}
          <button 
            onClick={() => {
              console.log('Current state:', { enterprises, locations, menuBoards });
              setApiErrors([]); // Clear errors
              loadEnterprises();
            }}
            style={{marginLeft: '10px', padding: '2px 8px'}}
          >
            Reload Enterprises
          </button>
        </div>

        {/* Filters */}
        <div className="configurator-filters">
          <div className="filters-section">
            <label>
              Enterprise: ({enterprises.length} available)
              <select
                value={selectedEnterpriseFilter}
                onChange={(e) => {
                  console.log('Enterprise changed to:', e.target.value);
                  setSelectedEnterpriseFilter(e.target.value);
                  setSelectedLocationFilter(''); // Reset location when enterprise changes
                }}
              >
                {!Array.isArray(enterprises) || enterprises.length === 0 ? (
                  <option value="">Loading enterprises...</option>
                ) : (
                  <>
                    <option value="">Select an Enterprise</option>
                    {Array.isArray(enterprises) && enterprises.map(enterprise => (
                      <option key={enterprise.enterpriseId} value={enterprise.enterpriseId}>
                        {enterprise.name}
                      </option>
                    ))}
                  </>
                )}
              </select>
            </label>
            
            <label>
              Location: ({locations.length} available)
              <select
                value={selectedLocationFilter}
                onChange={(e) => {
                  console.log('Location changed to:', e.target.value);
                  setSelectedLocationFilter(e.target.value);
                }}
              >
                <option value="">All Locations</option>
                {Array.isArray(locations) && locations.map(location => (
                  <option key={location.storeId} value={location.storeId}>
                    {location.storeName}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </div>

        <div className="configurator-body">
          {/* Sidebar */}
          <div className={`configurator-sidebar ${isSidebarCollapsed ? 'collapsed' : ''}`}>
            {/* Sidebar Toggle Button */}
            <button
              onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
              className="sidebar-toggle"
              style={{
                position: 'absolute',
                top: '10px',
                right: isSidebarCollapsed ? '5px' : '15px',
                zIndex: 10,
                background: 'var(--accent-primary)',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                padding: '8px 12px',
                cursor: 'pointer',
                fontSize: '0.9rem',
                fontWeight: '600',
                boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                transition: 'all 0.3s ease'
              }}
              title={isSidebarCollapsed ? 'Expand Menu' : 'Collapse Menu'}
            >
              {isSidebarCollapsed ? '> Menu' : '<'}
            </button>

            {!isSidebarCollapsed && (
              <>
                <div className="sidebar-section" style={{ marginTop: '50px' }}>
                  <h3>Menu Items</h3>
                  
                  {/* Search and filters */}
                  <div className="sidebar-filters">
                    <input
                      type="text"
                      placeholder="Search menu items..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="search-input"
                    />
                    <select
                      value={selectedCategory}
                      onChange={(e) => setSelectedCategory(e.target.value)}
                      className="category-select"
                    >
                      {categories.map(category => (
                        <option key={category} value={category}>{category}</option>
                      ))}
                    </select>
                  </div>

                  {/* Menu items list */}
                  <div className="menu-items-list">
                    {filteredMenuItems.map(item => (
                      <DraggableMenuItem key={item.menuItemId} item={item} />
                    ))}
                  </div>
                </div>

                {/* Media items */}
                <div className="sidebar-section">
                  <h3>Media</h3>
                  <div className="media-items">
                    <div className="draggable-media-item">
                      <div className="media-icon">VID</div>
                      <div className="media-label">Video Placeholder</div>
                    </div>
                    <div className="draggable-media-item">
                      <div className="media-icon">IMG</div>
                      <div className="media-label">Image Placeholder</div>
                    </div>
                    <div className="draggable-media-item">
                      <div className="media-icon">M</div>
                      <div className="media-label">Custom Text</div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Main content area */}
          <div className="configurator-main">
            {selectedBoard ? (
              <>
                <div className="board-info">
                  {isEditingBoardName ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                      <input
                        type="text"
                        value={editingBoardName}
                        onChange={(e) => setEditingBoardName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            saveBoardName();
                          } else if (e.key === 'Escape') {
                            setIsEditingBoardName(false);
                            setEditingBoardName('');
                          }
                        }}
                        style={{
                          fontSize: '1.75rem',
                          fontWeight: '600',
                          background: 'var(--surface-primary)',
                          border: '2px solid var(--accent-primary)',
                          borderRadius: '4px',
                          padding: '0.25rem 0.5rem',
                          color: 'var(--text-primary)',
                          flex: 1
                        }}
                        autoFocus
                      />
                      <button 
                        onClick={saveBoardName}
                        style={{
                          background: 'var(--accent-primary)',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          padding: '0.5rem 1rem',
                          cursor: 'pointer'
                        }}
                      >
                        Save
                      </button>
                      <button 
                        onClick={() => {
                          setIsEditingBoardName(false);
                          setEditingBoardName('');
                        }}
                        style={{
                          background: 'var(--surface-secondary)',
                          color: 'var(--text-primary)',
                          border: '1px solid var(--border-primary)',
                          borderRadius: '4px',
                          padding: '0.5rem 1rem',
                          cursor: 'pointer'
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <h2 
                      onClick={() => {
                        setIsEditingBoardName(true);
                        setEditingBoardName(selectedBoard.screenName);
                      }}
                      style={{
                        cursor: 'pointer',
                        margin: '0 0 0.5rem 0',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                      }}
                      title="Click to edit name"
                    >
                      {selectedBoard.screenName}
                      <span style={{ fontSize: '0.8rem', opacity: 0.6 }}>[EDIT]</span>
                    </h2>
                  )}
                  <div className="board-details">
                    <span className="board-type">{selectedBoard.screenType}</span>
                    <span className="board-grid">{selectedBoard.gridConfiguration.rows}×{selectedBoard.gridConfiguration.columns}</span>
                    <span className="board-ratio">{selectedBoard.gridConfiguration.aspectRatio}</span>
                  </div>
                </div>

                {/* Save Button - Always visible */}
                <div style={{ 
                  display: 'flex', 
                  gap: '1rem', 
                  marginBottom: '1rem',
                  padding: '0.75rem',
                  background: 'var(--surface-secondary)',
                  borderRadius: '8px',
                  border: '1px solid var(--border-primary)'
                }}>
                  <button
                    onClick={() => updateMenuBoard(selectedBoard)}
                    style={{
                      background: 'var(--accent-primary)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      padding: '0.75rem 1.5rem',
                      cursor: 'pointer',
                      fontWeight: '600',
                      fontSize: '0.9rem',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }}
                  >
                    Save Board Configuration
                  </button>
                  <div style={{
                    fontSize: '0.85rem',
                    color: 'var(--text-secondary)',
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    Save all changes including item positions and display settings
                  </div>
                </div>
                
                <div className="grid-container">
                  {renderGrid()}
                </div>

                {/* Drive-thru order confirmation overlay */}
                {selectedBoard.screenType === 'drive-thru' && selectedBoard.orderConfirmationBox?.enabled && (
                  <div 
                    className={`order-confirmation-overlay ${selectedBoard.orderConfirmationBox.position}`}
                    style={{
                      width: selectedBoard.orderConfirmationBox.width,
                      height: selectedBoard.orderConfirmationBox.height,
                      opacity: selectedBoard.orderConfirmationBox.opacity
                    }}
                  >
                    <div className="order-confirmation-header">Current Order</div>
                    <div className="order-confirmation-content">
                      <div className="order-item">Big Burger - $12.99</div>
                      <div className="order-item">Large Fries - $4.99</div>
                      <div className="order-total">Total: $17.98</div>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="no-board-selected">
                <div className="empty-state">
                  <h3>No Menu Board Selected</h3>
                  <p>Create a new menu board or select an existing one to get started.</p>
                  <button
                    className="btn btn-primary"
                    onClick={createNewMenuBoard}
                  >
Create New Menu Board
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Context Menu */}
        {contextMenu && (
          <ContextMenu
            x={contextMenu.x}
            y={contextMenu.y}
            onClose={() => setContextMenu(null)}
            onSelectDisplayType={handleDisplayTypeChange}
            currentDisplayType={contextMenu.item.displayType}
          />
        )}
      </div>

      <DragOverlay>
        {activeId ? (
          <div className="drag-overlay">
            {activeId.startsWith('menu-item-') ? (
              (() => {
                const menuItemId = activeId.replace('menu-item-', '');
                const draggedMenuItem = menuItems.find(item => item.menuItemId === menuItemId);
                return draggedMenuItem ? (
                  <div className="dragging-menu-item">
                    <div className="menu-item-image">
                      <EnhancedImage
                        src={draggedMenuItem.imageUrl}
                        alt={draggedMenuItem.name}
                        style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '4px' }}
                        placeholder="[FOOD]"
                      />
                    </div>
                    <div className="menu-item-info">
                      <div className="menu-item-name">{draggedMenuItem.name}</div>
                      <div className="menu-item-category">{draggedMenuItem.category}</div>
                      <div className="menu-item-price">${(draggedMenuItem.price || draggedMenuItem.basePrice || 0).toFixed(2)}</div>
                    </div>
                  </div>
                ) : (
                  <div className="dragging-menu-item">Menu Item</div>
                );
              })()
            ) : (
              <div className="dragging-item">Item</div>
            )}
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};

export default MenuBoardConfigurator;